import { Box, Typography } from "@mui/material";

import type { SKUStoreStatusBadgeItem } from "@/features/SKUStore/components/Status/statusBadgeItems";
import type { MouseEvent, ReactNode } from "react";

import { MuiTooltip } from "@/orion/MuiComponents/MuiTooltip";
import { visuallyHiddenSx } from "@/utils/visuallyHidden";

export type SKUStoreStatusBadgeTone =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral";

export type SKUStoreStatusBadgeProps = {
  label: ReactNode;
  tone: SKUStoreStatusBadgeTone;
  /** Optional hover detail (e.g. affected variant counts). */
  tooltip?: ReactNode;
  /** Makes the badge an actionable button (e.g. deep-link to a tab). */
  onClick?: () => void;
  /** Corner radius in px; a full pill when omitted. */
  radius?: number;
};

/** Every chip in a card's status row renders through this, so all share a height. */
const SKU_STORE_STATUS_BADGE_HEIGHT = 28;

/**
 * Amber-600 text on amber-50 (`warning.dark` on `warning.bgContainer`) is too
 * low-contrast at chip size, so the warning tone uses the palette's dark
 * neutral `contrastText` plus a tinted outline to still read as amber.
 */
function toneStyles(tone: SKUStoreStatusBadgeTone) {
  if (tone === "warning") {
    return {
      bgcolor: "warning.bgContainer",
      color: "warning.contrastText",
      border: "warning.customStateColor.outlinedBorder",
    };
  }
  if (tone === "neutral") {
    // Carries no status of its own (e.g. the "+2" overflow chip).
    return {
      bgcolor: "action.selected",
      color: "text.secondary",
      border: undefined,
    };
  }
  return {
    bgcolor: `${tone}.bgContainer`,
    color: `${tone}.dark`,
    border: undefined,
  };
}

export function SKUStoreStatusBadge({
  label,
  tone,
  tooltip,
  onClick,
  radius,
}: SKUStoreStatusBadgeProps) {
  const tokens = toneStyles(tone);
  const pill = (
    <Box
      component={onClick ? "button" : "span"}
      type={onClick ? "button" : undefined}
      onClick={
        onClick
          ? (event: MouseEvent) => {
              // Cards are click targets too; keep the chip's action distinct.
              event.stopPropagation();
              onClick();
            }
          : undefined
      }
      sx={(theme) => ({
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "flex-start",
        flexShrink: 0,
        height: SKU_STORE_STATUS_BADGE_HEIGHT,
        boxSizing: "border-box",
        bgcolor: tokens.bgcolor,
        border: tokens.border ? `1px solid` : "1px solid transparent",
        borderColor: tokens.border ?? "transparent",
        borderRadius:
          radius != null ? `${String(radius)}px` : theme.shape.borderRadiusLg,
        px: 3,
        py: 0,
        font: "inherit",
        cursor: onClick ? "pointer" : tooltip != null ? "default" : undefined,
        "&:hover": onClick ? { filter: "brightness(0.96)" } : undefined,
        "&:focus-visible": onClick
          ? {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            }
          : undefined,
      })}
    >
      <Typography
        variant="body3"
        component="span"
        sx={(theme) => ({
          color: tokens.color,
          fontWeight: theme.typography.fontWeightScale.semiBold,
          whiteSpace: "nowrap",
          lineHeight: 1,
        })}
      >
        {label}
      </Typography>
      {tooltip != null ? (
        // Tooltip detail for screen readers: the tooltip only opens on hover
        // (the pill is not focusable), and ARIA forbids MUI's default
        // aria-label on a generic <span>.
        <Box component="span" sx={visuallyHiddenSx}>
          , {tooltip}
        </Box>
      ) : null}
    </Box>
  );

  if (tooltip == null) {
    return pill;
  }
  return (
    // describeChild: never label the child with the tooltip text (see above).
    <MuiTooltip title={tooltip} placement="top" arrow describeChild>
      {pill}
    </MuiTooltip>
  );
}

/** A status row's chips in order, for rows that never fold. */
export function SKUStoreStatusBadgeList({
  items,
  radius,
}: {
  items: SKUStoreStatusBadgeItem[];
  radius?: number;
}) {
  return (
    <>
      {items.map(({ key, label, tone, tooltip, onClick }) => (
        <SKUStoreStatusBadge
          key={key}
          label={label}
          tone={tone}
          tooltip={tooltip}
          onClick={onClick}
          radius={radius}
        />
      ))}
    </>
  );
}
